import { memo, useCallback, useEffect, useState } from "react";
import Rating from "@mui/material/Rating";
import Pagination from "@mui/material/Pagination";
import { Stack } from "@mui/material";
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from "@mui/icons-material/StarBorder";

import { theme } from "../../theme";
import DivSpinner from "../../components/divSpinner";
import { fetchFtmRatings } from "./service";
import OffcanvasTemplate from "../../components/offcanvasTemplate";
import DropDown from "./DropDown";
import { formatDateInJobStatsLog } from "../../utils";

/* -------------------- Memoized UI Components -------------------- */
const FooterBtn = memo(({ totalPages, currentPage, onChange }) => (
    <Stack spacing={2} sx={{ alignItems: "center", justifyContent: "center", my: 3 }}>
        <Pagination
            count={totalPages}
            page={currentPage}
            onChange={onChange}
            color={theme.main}
        />
    </Stack>
));

const Loading = memo(() => (
    <div style={{ height: `${window.innerHeight - 387}px` }}>
        <DivSpinner />
    </div>
));

/* -------------------- Main Component -------------------- */
const Reviews = ({ ftm_id, open, close }) => {
    const [loading, setLoading] = useState(true);
    const [ftmRatings, setFtmRatings] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortByRating, setSortByRating] = useState("");
    const [filterByRating, setFilterByRating] = useState("");

    const handlePageChange = useCallback((_, page) => {
        setCurrentPage(page);
    }, []);

    const getFtmRatings = useCallback(async () => {
        if (!open || !ftm_id) return;

        setLoading(true);
        try {
            const response = await fetchFtmRatings(
                ftm_id,
                currentPage,
                sortByRating,
                filterByRating
            );

            if (response?.status === "Success") {
                setFtmRatings(response.data?.ratings || {});
                setReviews(response.data?.reviews || []);
                setTotalPages(response.total_pages || 0);
            }
        } catch (error) {
            console.error("Error fetching ratings:", error);
        } finally {
            setLoading(false);
        }
    }, [open, ftm_id, currentPage, sortByRating, filterByRating]);

    useEffect(() => {
        getFtmRatings();
    }, [getFtmRatings]);

    return (
        <OffcanvasTemplate
            open={open}
            handleClose={close}
            placement="end"
            title="Customer Reviews"
            body={
                <>
                    {loading && <Loading />}

                    {!loading && ftmRatings && (
                        <div className="card mb-2">
                            <div className="card-header p-2 ps-3">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <p className="text-sm mb-0">Total Reviews</p>
                                        <h4 className="mb-0">{ftmRatings.ftm_total_ratings}</h4>
                                    </div>
                                    <div className={`icon icon-md icon-shape bg-gradient-${theme.main} text-center border-radius-lg`}>
                                        <i className="material-icons opacity-10">reviews</i>
                                    </div>
                                </div>
                            </div>

                            <div className="card-footer py-2"/>
                        </div>
                    )}

                    {!loading && (
                        <div className="row align-items-center my-3">
                            <div className="col-6">
                                <DropDown
                                    btnName="Sort by Rating"
                                    list={[
                                        { label: "High to Low", value: "desc" },
                                        { label: "Low to High", value: "asc" },
                                    ]}
                                    selectedValue={sortByRating}
                                    setSelectedValue={setSortByRating}
                                />
                            </div>
                            <div className="col-6 text-end">
                                <DropDown
                                    btnName="Filter by Rating"
                                    list={[
                                        { label: "All Stars", value: "0" },
                                        { label: "5 Stars", value: "5" },
                                        { label: "4 Stars", value: "4" },
                                        { label: "3 Stars", value: "3" },
                                        { label: "2 Stars", value: "2" },
                                        { label: "1 Star", value: "1" },
                                    ]}
                                    selectedValue={filterByRating}
                                    setSelectedValue={setFilterByRating}
                                />
                            </div>
                        </div>
                    )}

                    {!loading && reviews.length === 0 && (
                        <div className="text-center my-5">
                            <h6 className="text-muted">No reviews found</h6>
                            <p className="text-sm text-secondary">
                                This provider hasn’t received any reviews yet.
                            </p>
                        </div>
                    )}

                    {!loading &&
                        reviews.map((review, index) => (
                            <div className={`card card-blog ${index ? "mt-3" : ""}`} key={index}>
                                <div className="card-body p-3">
                                    <p className="text-sm mb-0">
                                        {review?.job?.sub_skill?.skill?.skill_name} -{" "}
                                        {review?.job?.sub_skill?.sub_skill_name}
                                    </p>

                                    <h5 className="mt-2 mb-0">
                                        {review?.job?.customer?.customer_full_name}
                                    </h5>

                                    <p className="text-sm">
                                        {formatDateInJobStatsLog(
                                            review.job_customer_rating_timestamp
                                        )}
                                    </p>

                                    <p className="text-sm">
                                        {review.job_customer_rating_feedback}
                                    </p>

                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>{review?.job?.job_postcode}</span>
                                        <Rating
                                            value={Number(review.job_customer_rating_value)}
                                            readOnly
                                            sx={{ fontSize: "1.3rem" }}
                                            icon={<StarIcon sx={{ fontSize: "1.3rem", color: theme.main_code }} />}
                                            emptyIcon={<StarBorderIcon sx={{ fontSize: "1.3rem", color: "#ccc" }} />}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                </>
            }
            footer={
                !loading && reviews.length > 0 ? (
                    <FooterBtn
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onChange={handlePageChange}
                    />
                ) : null
            }
        />
    );
};

export default memo(Reviews);